import { capMatchPercent } from "./matchScoreUtils";

// ─── Feature-matching helpers (mirrors backend matchingEngine logic) ───────────

/**
 * Generic filler/qualifier words that describe a desire ("good camera") rather
 * than the feature itself ("camera"). Dropped before matching so "Long Battery
 * Life" correctly matches a "Battery Life" spec.
 */
const FEATURE_STOPWORDS = new Set([
  "the", "a", "an", "of", "with", "and", "or", "for", "to", "in", "on",
  "good", "great", "best", "high", "low", "fast", "long", "large", "small",
  "nice", "better",   "need", "needs", "want", "must", "have", "has", "required",
]);

/** Split a string into lowercased alphanumeric tokens (>= 2 chars). */
const tokenizeText = (str) =>
  String(str ?? "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2);

/** Tokenize a user-requested feature name, dropping generic filler words. */
const tokenizeFeature = (featureName) =>
  tokenizeText(featureName).filter((t) => !FEATURE_STOPWORDS.has(t));

/**
 * Evaluate how well a single product spec list matches a feature name.
 * Returns 95 (all-token label match), 90 (all-token value match),
 * 70 (partial token match), or 0 (no match).
 */
const evalFeatureVsSpecs = (featureName, specs) => {
  const featureTokens = tokenizeFeature(featureName);
  if (featureTokens.length === 0) return 0;

  let best = 0;
  for (const spec of specs) {
    const labelTokens = new Set(tokenizeText(spec.label || spec.key || ""));
    const valueTokens = new Set(tokenizeText(spec.value));

    if (featureTokens.every((t) => labelTokens.has(t))) { best = Math.max(best, 95); continue; }
    if (featureTokens.every((t) => valueTokens.has(t))) { best = Math.max(best, 90); continue; }
    if (featureTokens.some((t) => labelTokens.has(t) || valueTokens.has(t))) {
      best = Math.max(best, 70);
    }
  }
  return best;
};

// ──────────────────────────────────────────────────────────────────────────────

/**
 * Calculate match percentage and dimension scores for a product based on AI chat match data
 * This is a reusable utility that can be used in ProductCard and ProductDetailsPage
 *
 * Phase 4 Enhancement: Now includes matchingMetadata multipliers for feedback-based adjustments
 */
export const calculateMatchScore = (product, wizardData, specs = []) => {
  if (!wizardData || !product) return null;

  const dimensions = [];
  let matchScore = 0;
  let totalChecks = 0;
  const buyerType = wizardData.buyerType || "Individual";
  const isOrganization = buyerType === "Organization";
  const featurePriorities = wizardData.featurePriorities || {};
  const stretchPref = wizardData.stretch || "No";

  const stretchPercent =
    stretchPref === "Up to 10%" ? 0.1 : stretchPref === "Up to 20%" ? 0.2 : 0;

  // Budget check (different logic for organizations)
  if (wizardData.budget) {
    totalChecks++;
    const budget = wizardData.budget;
    const budgetMin = budget.min || 0;
    const budgetMax = budget.max || Infinity;
    const effectiveMax = budgetMax * (1 + stretchPercent || 0);
    let budgetScore = 0;

    if (isOrganization) {
      // Organizations: More flexible on budget if product offers value (warranty, support, etc.)
      // Check if product has warranty or support info
      const hasWarranty = specs.some(
        (s) =>
          (s.label || s.key || "").toLowerCase().includes("warranty") ||
          (s.label || s.key || "").toLowerCase().includes("support")
      );

      if (product.price >= budgetMin && product.price <= budgetMax) {
        matchScore++;
        budgetScore = hasWarranty ? 100 : 95; // Higher score if warranty included
      } else if (product.price < budgetMin) {
        matchScore += 0.5;
        budgetScore = hasWarranty ? 95 : 85; // Great value, especially with warranty
      } else {
        const overage = product.price - budgetMax;
        const budgetRange = budgetMax - budgetMin || 1000;
        // Organizations are more flexible - less penalty for going over
        budgetScore = hasWarranty
          ? Math.max(60, 100 - Math.round((overage / budgetRange) * 40))
          : Math.max(50, 100 - Math.round((overage / budgetRange) * 50));
      }
    } else {
      // Individual: Standard budget logic (matching ProductDetailsPage)
      if (product.price >= budgetMin && product.price <= budgetMax) {
        matchScore++;
        const hasExplicitMin =
          budget.min != null &&
          Number.isFinite(Number(budget.min)) &&
          Number(budget.min) > 0;
        const hasFiniteMax = Number.isFinite(budgetMax) && budgetMax > 0;
        // Keep in sync with backend matchingEngine.scoreMaxOnlyBudgetFit
        if (!hasExplicitMin && hasFiniteMax) {
          const ratio = product.price / budgetMax;
          if (ratio >= 0.5) budgetScore = 100;
          else if (ratio >= 0.35) budgetScore = 72;
          else if (ratio >= 0.2) budgetScore = 42;
          else budgetScore = 22;
        } else {
          budgetScore = 100;
        }
      } else if (product.price < budgetMin) {
        matchScore += 0.5;
        const hasExplicitMin =
          budget.min != null &&
          Number.isFinite(Number(budget.min)) &&
          Number(budget.min) > 0;
        if (hasExplicitMin && budgetMin > 0) {
          const ratio = product.price / budgetMin;
          if (ratio >= 0.85) budgetScore = 90;
          else if (ratio >= 0.65) budgetScore = 62;
          else if (ratio >= 0.4) budgetScore = 40;
          else budgetScore = 24;
        } else {
          budgetScore = 90;
        }
      } else {
        const overage = product.price - budgetMax;
        const budgetRange = budgetMax - budgetMin || 1000;
        budgetScore = Math.max(
          40,
          100 - Math.round((overage / budgetRange) * 60)
        );
      }
    }

    dimensions.push({
      name: isOrganization ? "Budget (TCO)" : "Budget",
      score: budgetScore,
    });
  }

  // Sub-category matching (e.g., Foldable, Gaming Phone)
  if (wizardData.subCategory) {
    totalChecks++;
    const subCatLower = String(wizardData.subCategory).toLowerCase();
    const productName = (product.name || "").toLowerCase();
    const productDesc = (product.description || "").toLowerCase();
    let subCatScore = 0;

    // Check product name/description for sub-category keywords
    if (subCatLower === "foldable") {
      const hasFoldable =
        productName.includes("fold") || productDesc.includes("fold");
      subCatScore = hasFoldable ? 100 : 40;
      if (hasFoldable) matchScore++;
    } else if (subCatLower.includes("gaming")) {
      const hasGaming =
        productName.includes("gaming") ||
        productDesc.includes("gaming") ||
        specs.some((s) =>
          (s.label || s.key || "").toLowerCase().includes("gaming")
        );
      subCatScore = hasGaming ? 100 : 50;
      if (hasGaming) matchScore++;
    } else if (subCatLower.includes("budget")) {
      // Budget phones: check if price is relatively low
      const avgPrice = wizardData.budget?.max || 1000;
      subCatScore = product.price < avgPrice * 0.6 ? 100 : 60;
      if (product.price < avgPrice * 0.6) matchScore++;
    } else if (subCatLower === "standard") {
      // Standard = no special form factor, so neutral score
      subCatScore = 85;
      matchScore += 0.85;
    } else {
      // Other sub-categories: check for keywords
      const hasMatch =
        productName.includes(subCatLower) || productDesc.includes(subCatLower);
      subCatScore = hasMatch ? 95 : 70;
      if (hasMatch) matchScore++;
    }

    dimensions.push({
      name: "Form Factor",
      score: subCatScore,
    });
  }

  // Brand preference
  const brandPreference = wizardData.brand;
  if (brandPreference && brandPreference !== "No Preference" && typeof brandPreference === "string") {
    totalChecks++;
    const productBrand = String(product.brand?.name || product.brand || "");
    let brandScore = 0;

    if (
      productBrand &&
      productBrand.toLowerCase().includes(brandPreference.toLowerCase())
    ) {
      matchScore++;
      brandScore = 100;
    } else {
      brandScore = 50; // Partial match if similar brand
    }

    dimensions.push({
      name: "Brand",
      score: brandScore,
    });
  }

  // Brand ecosystem integration (if brand was selected)
  if (
    brandPreference &&
    brandPreference !== "No Preference" &&
    typeof brandPreference === "string" &&
    wizardData.ecosystemIntegration &&
    Array.isArray(wizardData.ecosystemIntegration)
  ) {
    totalChecks++;
    const productBrand = String(product.brand?.name || product.brand || "");
    const productName = (product.name || "").toLowerCase();
    const productDesc = (product.description || "").toLowerCase();
    let ecosystemScore = 0;
    let matches = 0;

    wizardData.ecosystemIntegration.forEach((need) => {
      const needLower = need.toLowerCase();

      if (needLower.includes("same brand ecosystem")) {
        // Check if product is from the same brand
        if (
          productBrand &&
          productBrand.toLowerCase().includes(brandPreference.toLowerCase())
        ) {
          ecosystemScore += 30;
          matches++;
        }
      } else if (needLower.includes("cross-platform")) {
        // Check for cross-platform compatibility keywords
        const hasCrossPlatform =
          productDesc.includes("cross-platform") ||
          productDesc.includes("compatible") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("compatible")
          );
        if (hasCrossPlatform) {
          ecosystemScore += 25;
          matches++;
        }
      } else if (needLower.includes("cloud sync")) {
        // Check for cloud sync features
        const hasCloud =
          productDesc.includes("cloud") ||
          productDesc.includes("icloud") ||
          productDesc.includes("google drive") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("cloud")
          );
        if (hasCloud) {
          ecosystemScore += 25;
          matches++;
        }
      } else if (needLower.includes("not important")) {
        // Neutral score
        ecosystemScore += 20;
        matches++;
      }
    });

    const finalEcosystemScore =
      matches > 0
        ? Math.min(
            100,
            Math.round(
              (ecosystemScore / matches) *
                (matches / wizardData.ecosystemIntegration.length) *
                100
            )
          )
        : 70;

    if (finalEcosystemScore >= 80) matchScore++;
    else if (finalEcosystemScore >= 60) matchScore += 0.5;

    dimensions.push({
      name: "Ecosystem Integration",
      score: finalEcosystemScore,
    });
  }

  // Organization-specific priorities
  if (
    isOrganization &&
    wizardData.orgPriorities &&
    Array.isArray(wizardData.orgPriorities)
  ) {
    wizardData.orgPriorities.forEach((priority) => {
      totalChecks++;
      const priorityLower = priority.toLowerCase();
      let priorityScore = 0;

      // Check for warranty/support
      if (
        priorityLower.includes("warranty") ||
        priorityLower.includes("support")
      ) {
        const hasWarranty = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("warranty") ||
            (s.label || s.key || "").toLowerCase().includes("support")
        );
        priorityScore = hasWarranty ? 95 : 40;
        if (hasWarranty) matchScore++;
      }
      // Check for reliability
      else if (priorityLower.includes("reliability")) {
        // Check for brand reputation or reliability indicators
        const hasReliability = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("reliability") ||
            (s.label || s.key || "").toLowerCase().includes("durability")
        );
        priorityScore = hasReliability ? 90 : 60; // Assume decent reliability if not specified
        if (hasReliability) matchScore++;
      }
      // Check for security
      else if (priorityLower.includes("security")) {
        const hasSecurity = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("security") ||
            (s.label || s.key || "").toLowerCase().includes("encryption")
        );
        priorityScore = hasSecurity ? 95 : 50;
        if (hasSecurity) matchScore++;
      }
      // Scalability, Integration, Bulk Pricing - harder to check from specs
      else {
        priorityScore = 70; // Neutral score - assume possible
      }

      dimensions.push({
        name: priority,
        score: priorityScore,
      });
    });
  }

  // Feature matching (Battery, Camera, Performance, etc.) - for both types
  if (wizardData.features && Array.isArray(wizardData.features)) {
    wizardData.features.forEach((feature) => {
      totalChecks++;
      const matchLevel = evalFeatureVsSpecs(feature, specs);

      // 95 = all tokens matched in label (exact), 90 = matched in value,
      // 70 = partial token overlap, 0 = no match → 30 (base)
      const featureScore = matchLevel > 0 ? matchLevel : 30;
      if (featureScore >= 90) matchScore++;
      else if (featureScore >= 70) matchScore += 0.5;

      dimensions.push({
        name: feature,
        score: featureScore,
        weight: featurePriorities[feature] || 5,
      });
    });
  }

  // Work needs branching (if "Work" was selected in usage)
  if (wizardData.workNeeds && Array.isArray(wizardData.workNeeds)) {
    wizardData.workNeeds.forEach((need) => {
      totalChecks++;
      const needLower = need.toLowerCase();
      let workScore = 0;

      if (needLower.includes("video calls")) {
        // Check for video call specific features first
        const videoCallSpec = specs.find((s) => {
          const label = (s.label || s.key || "").toLowerCase();
          return (
            label.includes("video call") ||
            label.includes("front camera") ||
            label.includes("webcam") ||
            label.includes("video conferencing")
          );
        });

        // Fallback to any camera spec (inference: camera = video call capable)
        const cameraSpec = !videoCallSpec
          ? specs.find((s) =>
              (s.label || s.key || "").toLowerCase().includes("camera")
            )
          : null;

        const hasVideoCall = !!videoCallSpec || !!cameraSpec;
        workScore = videoCallSpec ? 95 : cameraSpec ? 85 : 60; // Higher score for explicit video call features
        if (hasVideoCall) matchScore++;
      } else if (needLower.includes("email")) {
        // Most devices support email, so high score
        workScore = 90;
        matchScore += 0.9;
      } else if (needLower.includes("multitasking")) {
        const hasMultitask = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("ram") ||
            (s.label || s.key || "").toLowerCase().includes("memory") ||
            (s.label || s.key || "").toLowerCase().includes("multitask")
        );
        workScore = hasMultitask ? 95 : 70;
        if (hasMultitask) matchScore++;
      } else if (needLower.includes("battery")) {
        const hasBattery = specs.some((s) =>
          (s.label || s.key || "").toLowerCase().includes("battery")
        );
        workScore = hasBattery ? 95 : 65;
        if (hasBattery) matchScore++;
      } else if (needLower.includes("security")) {
        const hasSecurity = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("security") ||
            (s.label || s.key || "").toLowerCase().includes("fingerprint") ||
            (s.label || s.key || "").toLowerCase().includes("face id")
        );
        workScore = hasSecurity ? 95 : 60;
        if (hasSecurity) matchScore++;
      } else if (needLower.includes("cloud")) {
        const hasCloud =
          (product.description || "").toLowerCase().includes("cloud") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("cloud")
          );
        workScore = hasCloud ? 90 : 70;
        if (hasCloud) matchScore++;
      } else {
        workScore = 75; // Neutral
      }

      dimensions.push({
        name: `Work: ${need}`,
        score: workScore,
      });
    });
  }

  // Portability needs branching (if "Travel" was selected in usage)
  if (
    wizardData.portabilityNeeds &&
    Array.isArray(wizardData.portabilityNeeds)
  ) {
    wizardData.portabilityNeeds.forEach((need) => {
      totalChecks++;
      const needLower = need.toLowerCase();
      let portabilityScore = 0;

      if (needLower.includes("lightweight")) {
        const weightSpec = specs.find((s) =>
          (s.label || s.key || "").toLowerCase().includes("weight")
        );
        // Assume lightweight if weight < 200g for phones, < 2kg for laptops
        const weightValue = weightSpec?.value || "";
        const weightNum = parseFloat(weightValue) || 0;
        const isLight =
          weightNum > 0 &&
          weightNum <
            (product.category?.name?.toLowerCase().includes("phone")
              ? 200
              : 2000);
        portabilityScore = isLight ? 95 : 70;
        if (isLight) matchScore++;
      } else if (needLower.includes("durable")) {
        const hasDurability = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("durable") ||
            (s.label || s.key || "").toLowerCase().includes("water") ||
            (s.label || s.key || "").toLowerCase().includes("ip")
        );
        portabilityScore = hasDurability ? 95 : 70;
        if (hasDurability) matchScore++;
      } else if (needLower.includes("compact")) {
        const sizeSpec = specs.find(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("size") ||
            (s.label || s.key || "").toLowerCase().includes("dimension")
        );
        // Assume compact if size info exists (hard to determine exact size)
        portabilityScore = sizeSpec ? 85 : 75;
        if (sizeSpec) matchScore += 0.85;
      } else if (needLower.includes("battery")) {
        const hasBattery = specs.some((s) =>
          (s.label || s.key || "").toLowerCase().includes("battery")
        );
        portabilityScore = hasBattery ? 95 : 70;
        if (hasBattery) matchScore++;
      } else if (needLower.includes("fast charging")) {
        const hasFastCharge = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("charging") ||
            (s.label || s.key || "").toLowerCase().includes("fast charge")
        );
        portabilityScore = hasFastCharge ? 95 : 70;
        if (hasFastCharge) matchScore++;
      } else if (needLower.includes("water")) {
        const hasWaterResist = specs.some(
          (s) =>
            (s.label || s.key || "").toLowerCase().includes("water") ||
            (s.label || s.key || "").toLowerCase().includes("ip") ||
            (s.label || s.key || "").toLowerCase().includes("resistant")
        );
        portabilityScore = hasWaterResist ? 95 : 70;
        if (hasWaterResist) matchScore++;
      } else {
        portabilityScore = 75; // Neutral
      }

      dimensions.push({
        name: `Portability: ${need}`,
        score: portabilityScore,
      });
    });
  }

  // Daily usage dimension (influences battery/performance)
  if (wizardData.dailyUsage) {
    const usage = String(wizardData.dailyUsage).toLowerCase();
    let usageScore = 80;

    const hasBatterySpec = specs.some((s) =>
      (s.label || s.key || "").toLowerCase().includes("battery")
    );

    if (usage.includes("more than 8")) {
      usageScore = hasBatterySpec ? 95 : 70;
    } else if (usage.includes("4–8") || usage.includes("4-8")) {
      usageScore = hasBatterySpec ? 90 : 75;
    } else {
      // <4 hours
      usageScore = 85;
    }

    dimensions.push({
      name: "Daily Usage",
      score: usageScore,
    });
  }

  // Technical preferences (Screen size, Storage, OS)
  if (wizardData.technicalPreferences) {
    const techPrefs = wizardData.technicalPreferences;
    const productName = (product.name || "").toLowerCase();
    const productDesc = (product.description || "").toLowerCase();

    // Screen size matching
    if (techPrefs.screenSize && techPrefs.screenSize !== "No preference") {
      totalChecks++;
      const screenSizePref = techPrefs.screenSize.toLowerCase();
      let screenScore = 0;

      // Find screen size spec
      const screenSpec = specs.find((s) => {
        const label = (s.label || s.key || "").toLowerCase();
        return (
          label.includes("screen") ||
          label.includes("display") ||
          label.includes("size")
        );
      });

      if (screenSpec) {
        const screenValue = String(screenSpec.value || "").toLowerCase();
        const screenNum = parseFloat(screenValue) || 0;

        if (screenSizePref.includes("small")) {
          // Small: <6" for phones, <14" for laptops, <44" for TVs
          const isSmall =
            screenNum > 0 &&
            ((product.category?.name?.toLowerCase().includes("phone") &&
              screenNum < 6) ||
              (product.category?.name?.toLowerCase().includes("laptop") &&
                screenNum < 14) ||
              (product.category?.name?.toLowerCase().includes("tv") &&
                screenNum < 44));
          screenScore = isSmall ? 95 : 70;
          if (isSmall) matchScore++;
        } else if (screenSizePref.includes("medium")) {
          // Medium: 6-6.5" for phones, 14-15" for laptops, 44-55" for TVs
          const isMedium =
            screenNum > 0 &&
            ((product.category?.name?.toLowerCase().includes("phone") &&
              screenNum >= 6 &&
              screenNum <= 6.5) ||
              (product.category?.name?.toLowerCase().includes("laptop") &&
                screenNum >= 14 &&
                screenNum <= 15) ||
              (product.category?.name?.toLowerCase().includes("tv") &&
                screenNum >= 44 &&
                screenNum <= 55));
          screenScore = isMedium ? 95 : 70;
          if (isMedium) matchScore++;
        } else if (screenSizePref.includes("large")) {
          // Large: >6.5" for phones, >15" for laptops, >55" for TVs
          const isLarge =
            screenNum > 0 &&
            ((product.category?.name?.toLowerCase().includes("phone") &&
              screenNum > 6.5) ||
              (product.category?.name?.toLowerCase().includes("laptop") &&
                screenNum > 15) ||
              (product.category?.name?.toLowerCase().includes("tv") &&
                screenNum > 55));
          screenScore = isLarge ? 95 : 70;
          if (isLarge) matchScore++;
        } else {
          screenScore = 80; // Neutral
        }
      } else {
        // No screen spec found - neutral score
        screenScore = 75;
      }

      dimensions.push({
        name: "Screen Size",
        score: screenScore,
      });
    }

    // Storage matching
    if (techPrefs.storage && techPrefs.storage !== "No preference") {
      totalChecks++;
      const storagePref = techPrefs.storage.toLowerCase();
      let storageScore = 0;

      // Find storage spec
      const storageSpec = specs.find((s) => {
        const label = (s.label || s.key || "").toLowerCase();
        return (
          label.includes("storage") ||
          label.includes("memory") ||
          label.includes("capacity")
        );
      });

      if (storageSpec) {
        const storageValue = String(storageSpec.value || "").toLowerCase();
        // Extract number (e.g., "128GB" -> 128, "1TB" -> 1024)
        const storageMatch = storageValue.match(/(\d+)\s*(gb|tb)/i);
        let storageNum = 0;
        if (storageMatch) {
          storageNum = parseInt(storageMatch[1]);
          if (storageMatch[2].toLowerCase() === "tb") {
            storageNum *= 1024; // Convert TB to GB
          }
        }

        // Parse preference (e.g., "128GB+" -> 128)
        const prefMatch = storagePref.match(/(\d+)\s*(gb|tb)?/i);
        const prefNum = prefMatch
          ? parseInt(prefMatch[1]) *
            (prefMatch[2]?.toLowerCase() === "tb" ? 1024 : 1)
          : 0;

        if (storageNum >= prefNum) {
          storageScore = 100; // Meets or exceeds requirement
          matchScore++;
        } else {
          // Partial match based on how close
          const diff = prefNum - storageNum;
          storageScore = Math.max(50, 100 - Math.round((diff / prefNum) * 50));
        }
      } else {
        storageScore = 70; // Neutral if no storage spec
      }

      dimensions.push({
        name: "Storage",
        score: storageScore,
      });
    }

    // OS matching (only if OS preference exists and no brand was selected)
    if (techPrefs.os && techPrefs.os !== "No preference") {
      totalChecks++;
      const osPref = techPrefs.os.toLowerCase();
      let osScore = 0;

      const productBrand = String(product.brand?.name || product.brand || "");
      const productOS = (
        product.os ||
        productDesc ||
        productName
      ).toLowerCase();

      // Check for OS keywords
      if (osPref.includes("ios")) {
        const hasIOS =
          productBrand.toLowerCase().includes("apple") ||
          productOS.includes("ios") ||
          productOS.includes("iphone");
        osScore = hasIOS ? 100 : 30;
        if (hasIOS) matchScore++;
      } else if (osPref.includes("android")) {
        const hasAndroid =
          productOS.includes("android") ||
          (productBrand && !productBrand.toLowerCase().includes("apple"));
        osScore = hasAndroid ? 100 : 40;
        if (hasAndroid) matchScore++;
      } else if (osPref.includes("windows")) {
        const hasWindows = productOS.includes("windows");
        osScore = hasWindows ? 100 : 40;
        if (hasWindows) matchScore++;
      } else if (osPref.includes("macos")) {
        const hasMacOS =
          productBrand.toLowerCase().includes("apple") ||
          productOS.includes("macos") ||
          productOS.includes("mac");
        osScore = hasMacOS ? 100 : 30;
        if (hasMacOS) matchScore++;
      } else if (osPref.includes("linux")) {
        const hasLinux = productOS.includes("linux");
        osScore = hasLinux ? 100 : 50;
        if (hasLinux) matchScore++;
      } else if (osPref.includes("chrome")) {
        const hasChromeOS =
          productOS.includes("chrome") || productOS.includes("chromebook");
        osScore = hasChromeOS ? 100 : 50;
        if (hasChromeOS) matchScore++;
      } else {
        osScore = 75; // Neutral
      }

      dimensions.push({
        name: "Operating System",
        score: osScore,
      });
    }
  }

  // Emerging features matching
  if (
    wizardData.emergingFeatures &&
    Array.isArray(wizardData.emergingFeatures)
  ) {
    wizardData.emergingFeatures.forEach((feature) => {
      totalChecks++;
      const featureLower = feature.toLowerCase();
      let emergingScore = 0;

      const productName = (product.name || "").toLowerCase();
      const productDesc = (product.description || "").toLowerCase();

      if (
        featureLower.includes("ai") ||
        featureLower.includes("smart assistant")
      ) {
        const hasAI =
          productName.includes("ai") ||
          productDesc.includes("ai") ||
          productDesc.includes("smart assistant") ||
          productDesc.includes("siri") ||
          productDesc.includes("google assistant") ||
          productDesc.includes("bixby") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("ai")
          );
        emergingScore = hasAI ? 95 : 60;
        if (hasAI) matchScore++;
      } else if (featureLower.includes("foldable")) {
        const hasFoldable =
          productName.includes("fold") ||
          productDesc.includes("fold") ||
          productDesc.includes("foldable");
        emergingScore = hasFoldable ? 100 : 50;
        if (hasFoldable) matchScore++;
      } else if (
        featureLower.includes("eco") ||
        featureLower.includes("sustainable")
      ) {
        const hasEco =
          productDesc.includes("eco") ||
          productDesc.includes("sustainable") ||
          productDesc.includes("recycled") ||
          productDesc.includes("carbon") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("eco")
          );
        emergingScore = hasEco ? 95 : 70;
        if (hasEco) matchScore++;
      } else if (featureLower.includes("wireless charging")) {
        const hasWireless =
          productDesc.includes("wireless charging") ||
          productDesc.includes("qi") ||
          productDesc.includes("mag safe") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("wireless")
          );
        emergingScore = hasWireless ? 95 : 65;
        if (hasWireless) matchScore++;
      } else if (featureLower.includes("5g")) {
        const has5G =
          productName.includes("5g") ||
          productDesc.includes("5g") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("5g")
          );
        emergingScore = has5G ? 95 : 60;
        if (has5G) matchScore++;
      } else if (featureLower.includes("ar") || featureLower.includes("vr")) {
        const hasARVR =
          productDesc.includes("ar") ||
          productDesc.includes("vr") ||
          productDesc.includes("augmented") ||
          productDesc.includes("virtual") ||
          specs.some(
            (s) =>
              (s.label || s.key || "").toLowerCase().includes("ar") ||
              (s.label || s.key || "").toLowerCase().includes("vr")
          );
        emergingScore = hasARVR ? 95 : 55;
        if (hasARVR) matchScore++;
      } else if (featureLower.includes("biometric")) {
        const hasBiometric =
          productDesc.includes("biometric") ||
          productDesc.includes("fingerprint") ||
          productDesc.includes("face id") ||
          productDesc.includes("face unlock") ||
          specs.some((s) =>
            (s.label || s.key || "").toLowerCase().includes("biometric")
          );
        emergingScore = hasBiometric ? 95 : 70;
        if (hasBiometric) matchScore++;
      } else {
        emergingScore = 75; // Neutral
      }

      dimensions.push({
        name: `Emerging: ${feature}`,
        score: emergingScore,
      });
    });
  }

  // Calculate overall percentage
  const percentage =
    totalChecks > 0 ? Math.round((matchScore / totalChecks) * 100) : 85;

  // Calculate simple average from dimensions (matching ProductDetailsPage logic)
  let simpleAvg =
    dimensions.length > 0
      ? Math.round(
          dimensions.reduce((sum, dim) => sum + dim.score, 0) /
            dimensions.length
        )
      : percentage;

  // Phase 4: Apply feedback-based multipliers from matchingMetadata
  let adjustedScore = simpleAvg;
  let multiplierInfo = null;

  if (product.matchingMetadata?.multipliers) {
    const { bonus, penalty, confidence } = product.matchingMetadata.multipliers;

    // Only apply multipliers if confidence is meaningful (>= 0.5)
    if (confidence >= 0.5) {
      // Apply bonus and penalty with confidence weighting
      const bonusEffect = 1 + (bonus - 1) * confidence;
      const penaltyEffect = penalty + (1 - penalty) * (1 - confidence);

      adjustedScore = Math.round(simpleAvg * bonusEffect * penaltyEffect);
      adjustedScore = capMatchPercent(adjustedScore);

      // Store multiplier info for transparency
      multiplierInfo = {
        originalScore: simpleAvg,
        bonus,
        penalty,
        confidence,
        adjustment: adjustedScore - simpleAvg,
      };
    }
  }

  // Add satisfaction info if available
  let satisfactionInfo = null;
  if (product.matchingMetadata?.satisfaction?.totalFeedback > 0) {
    satisfactionInfo = {
      overall: product.matchingMetadata.satisfaction.overall,
      totalFeedback: product.matchingMetadata.satisfaction.totalFeedback,
      valueForMoney: product.matchingMetadata.satisfaction.valueForMoney,
    };
  }

  return {
    percentage: capMatchPercent(adjustedScore),
    rawPercentage: simpleAvg, // Original score before multipliers
    dimensions: dimensions.sort((a, b) => b.score - a.score), // Sort by score descending
    multiplierInfo, // Info about applied multipliers (null if not applied)
    satisfactionInfo, // Satisfaction data from purchase feedback
  };
};

/**
 * Quick match calculation for badges (lightweight version)
 * Returns just the percentage without full dimension breakdown
 */
export const calculateQuickMatch = (product, wizardData, specs = []) => {
  const matchResult = calculateMatchScore(product, wizardData, specs);
  return matchResult ? matchResult.percentage : null;
};
